import { formatDistanceToNow } from "date-fns";

import { Conversation } from "@/pages/Private/pages/Apollo/schema/get-people";
import { TrackerSignalResult } from "@/pages/Private/pages/ProfileTracker/schema/trackerSignalResult";

/**
 * Conversation `type`s that count as an outbound action (a sent email / LinkedIn message). Scheduled
 * variants are excluded — the mockup's activity row is the last action the rep *actually* took.
 */
export const OUTBOUND_ACTIVITY_TYPES = ["mail_sent", "message_sent"];

/** The compact signal-tile visual, derived from the signal's channel / category. */
export type SignalIconKind = "linkedin" | "news" | "web" | "hire" | "generic";

export interface SignalView {
	title: string;
	snippet: string;
	timeIso?: string | null;
	iconKind: SignalIconKind;
}

export interface ActivityView {
	channel: "email" | "linkedin";
	/** i18n key for the row title (resolved with the lead's first name by the row component). */
	titleKey: string;
	snippet: string;
	timeIso: string;
}

/**
 * Strip HTML tags + decode the handful of entities that show up in stored email bodies, collapsing
 * whitespace so the result reads as a clean single-line snippet (the row clamps it to one line).
 */
export const stripHtml = (html: string): string =>
	html
		.replace(/<[^>]*>/g, " ")
		.replace(/&nbsp;/g, " ")
		.replace(/&amp;/g, "&")
		.replace(/&lt;/g, "<")
		.replace(/&gt;/g, ">")
		.replace(/&quot;/g, '"')
		.replace(/&#39;/g, "'")
		.replace(/\s+/g, " ")
		.trim();

/** Human "2 days ago" relative label; empty string for a missing/invalid date. */
export const formatRelativeTime = (iso?: string | null): string => {
	if (!iso) {
		return "";
	}

	const date = new Date(iso);

	if (Number.isNaN(date.getTime())) {
		return "";
	}

	return formatDistanceToNow(date, { addSuffix: true });
};

/**
 * The single most-recent outbound activity (sent email / LinkedIn message) with a non-empty body,
 * or `null` when the lead has no such conversation. Picks by latest `sentAt`.
 */
export const pickLatestOutboundActivity = (
	conversations?: Conversation[] | null
): Conversation | null => {
	if (!conversations?.length) {
		return null;
	}

	const outbound = conversations.filter(
		(conversation) =>
			!!conversation &&
			OUTBOUND_ACTIVITY_TYPES.includes(conversation.type) &&
			!!conversation.message?.trim()
	);

	if (!outbound.length) {
		return null;
	}

	return outbound.reduce((latest, conversation) =>
		new Date(conversation.sentAt).getTime() > new Date(latest.sentAt).getTime()
			? conversation
			: latest
	);
};

/** Map an outbound conversation to the compact activity-row view model. */
export const toActivityView = (conversation: Conversation): ActivityView => {
	const channel = conversation.type === "mail_sent" ? "email" : "linkedin";

	return {
		channel,
		titleKey:
			channel === "email"
				? "contacts.leadPanel.recent.activity.emailSent"
				: "contacts.leadPanel.recent.activity.linkedinSent",
		snippet: stripHtml(conversation.message ?? ""),
		timeIso: conversation.sentAt,
	};
};

/** Pick the tile visual for a signal: LinkedIn first, then category, else a generic bolt. */
export const getSignalIconKind = (signal: TrackerSignalResult): SignalIconKind => {
	if (signal.linkedinPost || signal.linkedinPostUrn || signal.linkedinCommentary) {
		return "linkedin";
	}

	const category = signal.signalCategory?.toLowerCase() ?? "";

	if (category.includes("industry")) {
		return "news";
	}

	if (category.includes("company")) {
		return "web";
	}

	if (category.includes("hir")) {
		return "hire";
	}

	return "generic";
};

/**
 * Map a raw {@link TrackerSignalResult} to the compact signal-row view model, falling back through
 * the scored/raw signal payloads for the title/snippet the top-level columns don't always carry.
 */
export const toSignalView = (signal: TrackerSignalResult): SignalView => ({
	title: signal.title || signal.scoredSignal?.title || signal.rawSignal?.title || "",
	snippet:
		signal.summary ||
		signal.linkedinPost?.commentary ||
		signal.linkedinCommentary ||
		signal.scoredSignal?.summary ||
		signal.rawSignal?.summary ||
		"",
	timeIso: signal.publishedDate,
	iconKind: getSignalIconKind(signal),
});
